import { useEffect, useMemo, useRef, useState } from 'react';
import { decodeJwt, decodeProtectedHeader, jwtVerify, createRemoteJWKSet } from 'jose';

import { useToast } from './Toast.jsx';
import {
  XIcon,
  CopyIcon,
  CheckIcon,
  LockIcon,
  AlertTriangleIcon,
  KeyIcon,
} from './icons';

/**
 * Browser-only JWT decoder (card 057).
 *
 * Mounted from ProjectAuth as a modal — operators wiring up the SDK
 * paste their IdP token, see the claims, and (optionally) verify the
 * signature against the project's JWKS without anything ever leaving
 * their browser.
 *
 * Security posture (matches the card's constraints):
 *   - No fetch / XHR to Spotlight's API.  Only outbound traffic is
 *     the optional JWKS fetch, and that goes straight to the
 *     operator's identity provider URL (whatever they configured in
 *     ProjectAuth) — never proxied through us.
 *   - No console.log of the token, ever.  Even in catch blocks we log
 *     the error message, not the input.
 *   - No localStorage / sessionStorage write of the token.  State is
 *     React-only and dies when the modal closes.  We also clear it
 *     on unmount as belt+braces.
 *   - 8 KB cap on pasted text — covers normal JWTs (~1-4 KB) with
 *     headroom and prevents a malicious paste from pinning the main
 *     thread inside base64-decode / JSON.parse.
 *   - Signature verification uses jose.jwtVerify (Clerk-style) over a
 *     createRemoteJWKSet, never a hand-rolled HMAC / RSA path.
 *   - "Copy as cURL header" emits a single `-H "Authorization: Bearer
 *     <token>"` line.  The clipboard text is the value the operator
 *     will paste into a terminal; we DO NOT prepend `curl` or auto-
 *     execute anything, so even if their clipboard pipes into a
 *     shell pasta accident it can't run unprompted.
 */

const MAX_INPUT_BYTES = 8 * 1024;

const HIGHLIGHTED_CLAIMS = [
  'sub',
  'email',
  'email_verified',
  'aud',
  'iss',
  'exp',
  'nbf',
  'iat',
  'permissions',
  'scope',
  'scopes',
  'role',
  'roles',
];

export function JwtDecoder({
  open,
  onClose,
  jwksUrl = '',
  issuer = '',
  audience = '',
}) {
  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState(null);
  const [verifyState, setVerifyState] = useState({ status: 'idle', error: null });
  const textareaRef = useRef(null);
  const { showToast } = useToast();

  // Clear all state when the modal closes — token must not linger.
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => textareaRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    setRaw('');
    setParseError(null);
    setVerifyState({ status: 'idle', error: null });
    return undefined;
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const trimmed = raw.trim();
  const tooBig = trimmed.length > MAX_INPUT_BYTES;

  const decoded = useMemo(() => {
    if (!trimmed) return null;
    if (tooBig) return null;
    try {
      const header = decodeProtectedHeader(trimmed);
      const payload = decodeJwt(trimmed);
      return { header, payload };
    } catch (e) {
      return { error: e?.message || 'Could not decode token' };
    }
  }, [trimmed, tooBig]);

  useEffect(() => {
    if (!decoded) {
      setParseError(null);
      return;
    }
    setParseError(decoded.error || null);
  }, [decoded]);

  async function verifySignature() {
    if (!jwksUrl || !decoded || decoded.error) return;
    setVerifyState({ status: 'verifying', error: null });
    try {
      // createRemoteJWKSet fetches directly from the operator's IdP
      // (via global fetch). The URL is operator-configured — we do
      // NOT proxy through Spotlight's API.
      const jwks = createRemoteJWKSet(new URL(jwksUrl));
      const verifyOpts = {};
      if (issuer) verifyOpts.issuer = issuer;
      if (audience) verifyOpts.audience = audience;
      await jwtVerify(trimmed, jwks, verifyOpts);
      setVerifyState({ status: 'verified', error: null });
    } catch (e) {
      setVerifyState({
        status: 'failed',
        // jose surfaces structured errors — we surface .message only.
        // We deliberately do NOT log e or trimmed anywhere.
        error: e?.message || 'Verification failed',
      });
    }
  }

  async function copyJson() {
    if (!decoded || decoded.error) return;
    const text = JSON.stringify(
      { header: decoded.header, payload: decoded.payload },
      null,
      2,
    );
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: 'Copied as JSON',
        variant: 'success',
        autoHideMs: 2500,
      });
    } catch {
      showToast({
        title: 'Clipboard blocked',
        body: 'Your browser refused clipboard access. Try selecting the panel text manually.',
        variant: 'warning',
      });
    }
  }

  async function copyCurlHeader() {
    if (!trimmed || tooBig) return;
    // `-H "Authorization: Bearer <token>"` — meant to be pasted into
    // an existing curl command. We deliberately do not produce a
    // full `curl ...` line: that would tempt the operator to send
    // the bearer token to some URL hard-coded by us, and our copy
    // text is the worst place to embed an endpoint.
    const line = `-H "Authorization: Bearer ${trimmed}"`;
    try {
      await navigator.clipboard.writeText(line);
      showToast({
        title: 'Copied cURL header',
        body: 'Paste into your existing curl command — we did NOT include a URL.',
        variant: 'success',
        autoHideMs: 3500,
      });
    } catch {
      showToast({
        title: 'Clipboard blocked',
        body: 'Your browser refused clipboard access.',
        variant: 'warning',
      });
    }
  }

  if (!open) return null;

  return (
    <div
      className="jwt-decoder__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jwt-decoder-title"
      onClick={onClose}
    >
      <div
        className="jwt-decoder"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jwt-decoder__head">
          <h3 id="jwt-decoder-title">
            <KeyIcon size={16} /> Decode a JWT
          </h3>
          <button
            type="button"
            className="btn btn--icon"
            onClick={onClose}
            aria-label="Close decoder"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="jwt-decoder__banner" role="note">
          <LockIcon size={14} />
          <span>
            Decoded locally in your browser. The token is never sent
            to Spotlight — the only optional network call is the JWKS
            fetch to your own identity provider.
          </span>
        </div>

        <div className="jwt-decoder__body">
          <label className="field">
            <span className="field__label">Paste your JWT</span>
            <textarea
              ref={textareaRef}
              className="input jwt-decoder__textarea"
              value={raw}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              // A token in a textarea is not a password, but we still
              // mark it as sensitive so password managers don't try to
              // capture it on submit.
              data-1p-ignore="true"
              data-lpignore="true"
              placeholder="eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.…"
              onChange={(e) => setRaw(e.target.value)}
              rows={4}
            />
            <span className="jwt-decoder__hint">
              {tooBig
                ? `Input is ${trimmed.length.toLocaleString()} chars — capped at ${MAX_INPUT_BYTES.toLocaleString()}.`
                : `${trimmed.length.toLocaleString()} / ${MAX_INPUT_BYTES.toLocaleString()} chars`}
            </span>
          </label>

          {tooBig && (
            <div className="jwt-decoder__error" role="alert">
              <AlertTriangleIcon size={14} />
              <span>
                Pasted text exceeds {MAX_INPUT_BYTES.toLocaleString()}{' '}
                bytes. JWTs are normally 1–4 KB; refusing to decode to
                avoid pinning your browser on a malicious paste.
              </span>
            </div>
          )}

          {parseError && !tooBig && (
            <div className="jwt-decoder__error" role="alert">
              <AlertTriangleIcon size={14} />
              <span>{parseError}</span>
            </div>
          )}

          {decoded && !decoded.error && !tooBig && (
            <>
              <ClaimPanel title="Header" value={decoded.header} highlight={[]} />
              <ClaimPanel
                title="Payload"
                value={decoded.payload}
                highlight={HIGHLIGHTED_CLAIMS}
              />

              <div className="jwt-decoder__signature">
                <div className="jwt-decoder__signature-head">
                  <strong>Signature</strong>
                  {jwksUrl ? (
                    <span className="jwt-decoder__hint">
                      Will fetch <code>{jwksUrl}</code> directly from your
                      browser.
                    </span>
                  ) : (
                    <span className="jwt-decoder__hint">
                      No JWKS URL configured on this project — save one
                      under Identity provider above to enable signature
                      checks.
                    </span>
                  )}
                </div>
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn"
                    onClick={verifySignature}
                    disabled={!jwksUrl || verifyState.status === 'verifying'}
                  >
                    <LockIcon size={14} />{' '}
                    {verifyState.status === 'verifying'
                      ? 'Verifying…'
                      : 'Verify signature'}
                  </button>
                  <VerifyBadge state={verifyState} />
                </div>
                {verifyState.status === 'failed' && verifyState.error && (
                  <div
                    className="jwt-decoder__error"
                    role="alert"
                    style={{ marginTop: 8 }}
                  >
                    <AlertTriangleIcon size={14} />
                    <span>{verifyState.error}</span>
                  </div>
                )}
              </div>

              <ExpClaimAlert payload={decoded.payload} />
            </>
          )}
        </div>

        <div className="jwt-decoder__foot">
          <div className="jwt-decoder__foot-meta">
            {decoded && !decoded.error && !tooBig && (
              <>
                <code>alg={String(decoded.header?.alg || '?')}</code>
                {decoded.header?.kid && (
                  <code style={{ marginLeft: 8 }}>
                    kid={String(decoded.header.kid)}
                  </code>
                )}
              </>
            )}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <CopyButton
              label="Copy as JSON"
              disabled={!decoded || !!decoded.error || tooBig}
              onClick={copyJson}
            />
            <CopyButton
              label="Copy as cURL header"
              disabled={!trimmed || tooBig}
              onClick={copyCurlHeader}
            />
            <button type="button" className="btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>

        <style>{JWT_DECODER_CSS}</style>
      </div>
    </div>
  );
}

function ClaimPanel({ title, value, highlight }) {
  const json = useMemo(() => formatClaims(value, highlight), [value, highlight]);
  return (
    <div className="jwt-decoder__panel">
      <div className="jwt-decoder__panel-head">{title}</div>
      <pre className="jwt-decoder__panel-body">
        <code>{json}</code>
      </pre>
    </div>
  );
}

function formatClaims(value, highlight) {
  // String-rendered `<pre>` keeps every byte of the payload escaped
  // by React's text-node handling. No XSS risk from a claim like
  // `iss: "<script>"`.
  if (value == null || typeof value !== 'object') {
    return String(value);
  }
  const keys = Object.keys(value);
  if (keys.length === 0) return '{}';
  const known = highlight.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !highlight.includes(k));
  const ordered = [...known, ...rest];
  const lines = ['{'];
  ordered.forEach((k, i) => {
    const v = value[k];
    let rendered;
    if (k === 'exp' || k === 'iat' || k === 'nbf') {
      rendered = formatTimeClaim(v);
    } else {
      rendered = (JSON.stringify(v, null, 2) ?? 'undefined').replace(
        /\n/g,
        '\n  ',
      );
    }
    const comma = i === ordered.length - 1 ? '' : ',';
    lines.push(`  ${JSON.stringify(k)}: ${rendered}${comma}`);
  });
  lines.push('}');
  return lines.join('\n');
}

function formatTimeClaim(v) {
  if (typeof v !== 'number') return JSON.stringify(v);
  const date = new Date(v * 1000);
  if (Number.isNaN(date.getTime())) return JSON.stringify(v);
  return `${v}  // ${date.toISOString()}`;
}

function VerifyBadge({ state }) {
  if (state.status === 'idle') return null;
  if (state.status === 'verifying') {
    return <span className="badge badge--default">Checking…</span>;
  }
  if (state.status === 'verified') {
    return (
      <span
        className="badge"
        style={{
          background: 'var(--success-50, #ecfdf5)',
          color: 'var(--success-700, #047857)',
        }}
      >
        <CheckIcon size={12} /> Signature valid
      </span>
    );
  }
  return (
    <span
      className="badge badge--danger"
      style={{
        background: 'var(--danger-50, #fef2f2)',
        color: 'var(--danger-700, #b91c1c)',
      }}
    >
      <AlertTriangleIcon size={12} /> Signature invalid
    </span>
  );
}

function ExpClaimAlert({ payload }) {
  if (!payload || typeof payload !== 'object') return null;
  const now = Math.floor(Date.now() / 1000);
  const exp = typeof payload.exp === 'number' ? payload.exp : null;
  const nbf = typeof payload.nbf === 'number' ? payload.nbf : null;
  if (exp && exp < now) {
    const ago = secondsHuman(now - exp);
    return (
      <div className="jwt-decoder__error" role="status">
        <AlertTriangleIcon size={14} />
        <span>Token expired {ago} ago.</span>
      </div>
    );
  }
  if (nbf && nbf > now) {
    const inN = secondsHuman(nbf - now);
    return (
      <div className="jwt-decoder__error" role="status">
        <AlertTriangleIcon size={14} />
        <span>Token not yet valid — `nbf` is {inN} from now.</span>
      </div>
    );
  }
  return null;
}

function secondsHuman(s) {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function CopyButton({ label, onClick, disabled }) {
  const [copied, setCopied] = useState(false);
  async function handle() {
    if (disabled) return;
    await onClick();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button type="button" className="btn" onClick={handle} disabled={disabled}>
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />} {label}
    </button>
  );
}

const JWT_DECODER_CSS = `
  .jwt-decoder__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    padding: 16px;
  }
  .jwt-decoder {
    background: var(--surface-0);
    border-radius: 12px;
    width: 100%;
    max-width: 720px;
    max-height: calc(100vh - 32px);
    overflow: auto;
    box-shadow: 0 24px 48px -16px rgba(15, 23, 42, 0.4);
    display: flex;
    flex-direction: column;
  }
  .jwt-decoder__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border-1);
  }
  .jwt-decoder__head h3 {
    margin: 0;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .jwt-decoder__banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: var(--surface-1);
    color: var(--text-2);
    font-size: 13px;
    border-bottom: 1px solid var(--border-1);
  }
  .jwt-decoder__body {
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .jwt-decoder__textarea {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    line-height: 1.45;
    width: 100%;
    resize: vertical;
    min-height: 84px;
  }
  .jwt-decoder__hint {
    color: var(--text-3);
    font-size: 12px;
    margin-top: 4px;
  }
  .jwt-decoder__error {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    background: var(--danger-50, #fef2f2);
    color: var(--danger-700, #b91c1c);
    border-radius: 8px;
    font-size: 13px;
  }
  .jwt-decoder__panel {
    border: 1px solid var(--border-1);
    border-radius: 8px;
    overflow: hidden;
  }
  .jwt-decoder__panel-head {
    padding: 6px 12px;
    background: var(--surface-1);
    border-bottom: 1px solid var(--border-1);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-3);
  }
  .jwt-decoder__panel-body {
    margin: 0;
    padding: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    background: var(--surface-0);
    color: var(--text-1);
    max-height: 240px;
    overflow: auto;
  }
  .jwt-decoder__signature {
    border: 1px solid var(--border-1);
    border-radius: 8px;
    padding: 10px 12px;
    background: var(--surface-1);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .jwt-decoder__signature-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .jwt-decoder__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid var(--border-1);
    background: var(--surface-1);
  }
  .jwt-decoder__foot-meta {
    font-size: 12px;
    color: var(--text-3);
  }
`;
